/**
 *
 * @author: Matthew Rawson @ 15/08/2017 16:38
 * @description: Component to wrap react-slick component
 * @notes:
 *
 * # This component wraps: https://github.com/akiran/react-slick
 * For a full list of settings available.
 *
 *
 *
 * # Small workaround
 * A small work around was put in to get the custom SliderNav components to work properly.
 * For some reason the slidesToShow property isn't auto passed in by the SlickSlider component,
 * so we are manually passing them in as props. This is required to hide / show each SliderNav if on
 * the first or last scrolled page.
 *
 *
 * # Custom CSS to effect the slick slider and its slider track:
 * Pass this into the Slider component as the property 'className' this gets applied to the child SlickSlider.
 *
 * .slick-slider.{custom class name} {}
 * .slick-slider.{custom class name} .slick-track {}
 *
 *
 * # Example responsive settings:
 * import {DESKTOP, TABLET} from "../global/media"; // media query breakpoints
 * {
 * responsive: [
 *   {breakpoint: TABLET, settings: {slidesToShow: 1, slidesToScroll: 1}},
 *   {breakpoint: DESKTOP, settings: {slidesToShow: 2, slidesToScroll: 2}}
 * ]
 * }
 *
 * The responsive guidelines work mobile first, the breakpoint being the max-width those responsive settings
 * apply to. Outside of the responsive breakpoints the slider reverts back to previously defined settings.
 *
 */
import React, {Component} from "react"
import SlickSlider from "react-slick"
import {extend} from "lodash"

import id from "@irati/utils/id"

import SliderNav from "./SliderNav"

import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"

import GlobalStyle from "./styled"

interface Props {
  slidesToShow?: number
  settings: any
  className?: string
  initialSlide?: number
  id?: string
  onClick?: () => void
  maintainSameHeight?: boolean
}

class Slider extends Component<Props> {
  static defaultProps = {
    slidesToShow: 3,
    showButtons: true,
    onClick: () => {},
    maintainSameHeight: false,
    initialSlide: 0
  }

  defaultSettings = {}

  constructor(props: Props) {
    super(props)
    /** Default settings can be overridden by props.settings object */
    this.defaultSettings = {
      dots: false,
      infinite: false,
      centerMode: false,
      speed: 500,
      draggable: false,
      showButtons: true,
      initialSlide: props.initialSlide >= 0 ? props.initialSlide : 0,
      slidesToShow: props.slidesToShow,
      slidesToScroll: props.slidesToShow,
      className: props.className || ""
    }
  }

  getSlidesToShow = ({responsive, slidesToShow}) => {
    if (!responsive) {
      return slidesToShow
    }
    return responsive.reduce((currentSlidesToShow, responsiveSetting) => {
      if (currentSlidesToShow === slidesToShow && responsiveSetting.breakpoint > window.innerWidth) {
        return responsiveSetting.settings.slidesToShow
      }
      return currentSlidesToShow
    }, slidesToShow)
  }

  slickSlider = React.createRef()
  render() {
    const idNamespace = id("slider", this.props)

    /** Merge settings from props and defaults */
    let settings = extend(this.defaultSettings, this.props.settings)

    const alwaysShow = settings.centerMode && settings.infinite
    const mandatorySettings = {
      prevArrow: alwaysShow ? null : (
        <SliderNav
          id={id("nav-left", idNamespace)}
          slidesToShow={this.getSlidesToShow(settings)}
          alwaysShow={alwaysShow}
          show={settings.showButtons}
          previousNav
        />
      ),
      nextArrow: alwaysShow ? null : (
        <SliderNav
          id={id("nav-right", idNamespace)}
          slidesToShow={this.getSlidesToShow(settings)}
          alwaysShow={alwaysShow}
          show={settings.showButtons}
        />
      )
    }

    /** final settings */
    settings = extend(settings, mandatorySettings)

    return (
      <>
        <GlobalStyle maintainSameHeight={this.props.maintainSameHeight} />
        <SlickSlider id={idNamespace} {...settings} ref={this.slickSlider} afterChange={this.props.onClick}>
          {this.props.children || <div />}
        </SlickSlider>
      </>
    )
  }
}

export default Slider
